import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  Close,
  Person,
  Email,
  Phone,
  LocationOn,
  Edit,
  Business,
  Assignment,
  Receipt,
  ContactPhone,
  Description,
  History,
  Add,
  Home,
  ChevronRight,
  KeyboardArrowDown as ArrowDownIcon,
  Groups as GroupsIcon,
  Block,
  CheckCircle,
  AccountBalance as BalanceIcon,
  SupportAgent as SupportAgentIcon,
  Chat as ChatIcon,
  HeadsetMic as HeadsetMicIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import { useAuth, usePermissions } from '../contexts/AuthContext';
import { clientService } from '../services/clientService';
import { contactService } from '../services/contactService';
import { Client, Contact } from '../types';
import ClientForm from '../components/forms/ClientForm';
import NovoAtendimentoClienteDialog from './atendimento/componentes/NovoAtendimentoClienteDialog';
import { atendimentoService, AtendimentoTicket } from '../services/atendimentoService';
import ChamadoForm from '../components/forms/ChamadoForm';
import TabbedContractForm from '../components/forms/TabbedContractForm';
import InvoiceMultipleForm from '../components/forms/InvoiceMultipleForm';
import { contractService } from '../services/contractService';

// Componentes das abas - Usando barrel export
import {
  ClientOverview,
  ClientContacts,
  ClientContracts,
  ClientTickets,
  ClientAtendimentos,
  ClientFiles,
  ClientLogs,
  ClientMessages,
  ClientNfcomNotasTable,
} from '../components/client';
import ClientIndicacoes from '../components/client/ClientIndicacoes';
import ClientDocuments from '@/components/client/ClientDocuments';
import ClientFinanceiro from '../components/client/ClientFinanceiro';
import { formatarPhone, formatarCPFCNPJ } from '../utils/formatToView';
import { getStatusColor, getStatusLabel } from '../utils/contractStatusUtils';
import { detailPageHeaderPaperSx, detailPageTabsSx } from '../styles/theme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `client-tab-${index}`,
    'aria-controls': `client-tabpanel-${index}`,
  };
}

export interface ClientDetailsPageProps {
  clientId?: number;
  embedded?: boolean;
  hideEmbeddedClose?: boolean;
  onClose?: () => void;
}

const ClientDetailsPage: React.FC<ClientDetailsPageProps> = ({
  clientId: clientIdProp,
  embedded = false,
  hideEmbeddedClose = false,
  onClose,
}) => {
  const { id } = useParams<{ id: string }>();
  const resolvedClientId = clientIdProp ?? (id ? parseInt(id, 10) : undefined);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const theme = useTheme();
  const podeCriarNovoTicket = can('atendimento.criar_novo_ticket');

  const [client, setClient] = useState<Client | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [novoAtendimentoAberto, setNovoAtendimentoAberto] = useState(false);
  const [ticketAtendimentoAtivo, setTicketAtendimentoAtivo] = useState<AtendimentoTicket | null>(null);
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);

  // Menu Ações
  const [acoesMenuAnchor, setAcoesMenuAnchor] = useState<null | HTMLElement>(null);
  const acoesMenuOpen = Boolean(acoesMenuAnchor);

  useEffect(() => {
    if (resolvedClientId) {
      loadClient(resolvedClientId);
    }
  }, [resolvedClientId]);

  const loadClient = async (clientId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Carregar dados do cliente, contatos e ticket de atendimento em paralelo
      const [clientData, contactsResponse, ticketsResponse] = await Promise.all([
        clientService.getById(clientId),
        contactService.getByClientId(clientId).catch(() => ({ data: [] })),
        atendimentoService.getTickets({ cliente_id: clientId }).catch(() => []),
      ]);

      setClient(clientData);
      setContacts(contactsResponse.data || []);
      const ticketAtivo = (ticketsResponse || []).find(
        (ticket: AtendimentoTicket) => ticket.status !== 'fechado' && ticket.status !== 'fechado_bot'
      ) || null;
      setTicketAtendimentoAtivo(ticketAtivo);
    } catch (err: any) {
      console.error('Erro ao carregar cliente:', err);
      setError('Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  // Função para obter email principal do cliente
  const getPrimaryEmail = (): string => {
    const emailContact = contacts.find(contact => contact.tipo === 'email');
    return emailContact?.email || 'Não informado';
  };

  // Função para obter celular principal do cliente
  const getPrimaryCelular = (): string => {
    const celularContact = contacts.find(contact => contact.tipo === 'celular');
    return celularContact?.numero ? formatarPhone(celularContact.numero) : 'Não informado';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    if (embedded && onClose) {
      onClose();
      return;
    }
    navigate('/clients');
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleClientUpdate = (updatedClient: Client) => {
    setClient(updatedClient);
    setIsEditing(false);
    toast.success('Cliente atualizado com sucesso!');
  };

  const handleDeactivate = async () => {
    try {
      // 1. Verificar pendências primeiro
      const checkResponse = await clientService.checkPendencias(client!.id);
      const pendencias = checkResponse.data;

      if (pendencias.has_pendencias) {
        const confirm = await Swal.fire({
          title: 'Pendências Encontradas',
          html: `
            <div style="text-align: left;">
              <p>O cliente possui pendências que podem impedir a desativação:</p>
              <ul>
                ${pendencias.total_faturas > 0 ? `<li><strong>${pendencias.total_faturas}</strong> Fatura(s) pendente(s)</li>` : ''}
                ${pendencias.total_chamados > 0 ? `<li><strong>${pendencias.total_chamados}</strong> Chamado(s) aberto(s)/em andamento</li>` : ''}
              </ul>
              <p>Deseja continuar mesmo assim?</p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, continuar',
          cancelButtonText: 'Não, cancelar',
          confirmButtonColor: '#d32f2f',
        });

        if (!confirm.isConfirmed) return;
      }

      // 2. Pedir motivo da desativação
      const { value: motivo } = await Swal.fire({
        title: 'Desativar Cliente',
        text: `Informe o motivo para desativar o cliente "${client?.nome}":`,
        input: 'text',
        inputPlaceholder: 'Digite o motivo da desativação...',
        inputValidator: (value) => {
          if (!value || value.trim().length === 0) {
            return 'O motivo da desativação é obrigatório!';
          }
          if (value.trim().length < 10) {
            return 'O motivo deve ter pelo menos 10 caracteres!';
          }
        },
        showCancelButton: true,
        confirmButtonText: 'Desativar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d32f2f',
        showLoaderOnConfirm: true,
        preConfirm: async (motivo) => {
          try {
            // Chama desativar com confirmação de pendências = true
            const response = await clientService.desativar(client!.id, motivo, true);
            return response;
          } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao desativar cliente';
            Swal.showValidationMessage(errorMsg);
            throw error;
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      if (motivo) {
        toast.success('Cliente desativado com sucesso!');
        if (embedded && onClose) {
          onClose();
        } else {
          navigate('/clients');
        }
      }
    } catch (err: any) {
      console.error('Erro ao processar desativação:', err);
      toast.error('Erro ao processar desativação do cliente');
    }
  };

  // Função para exibir erros de desativação usando SweetAlert2
  const showDeactivateError = (errorData: any) => {
    // Fechar o diálogo de confirmação antes de mostrar o erro
    let htmlContent = `
      <div style="text-align: left;">
        <div style="margin-bottom: 20px; color: #d32f2f; font-weight: bold;">
          ⚠️ Existem pendências que impedem a desativação do cliente.
        </div>
    `;

    // Seção de faturas
    if (errorData.faturas) {
      htmlContent += `
        <div style="margin-bottom: 20px;">
          <div style="color: #d32f2f; font-weight: bold; margin-bottom: 10px;">
            📄 Faturas Pendentes (${errorData.faturas.length})
          </div>
          <div style="color: #666; margin-bottom: 10px;">
            As seguintes faturas devem ser quitadas antes da desativação:
          </div>
      `;

      errorData.faturas.forEach((fatura: any) => {
        const valor = parseFloat(fatura.valor).toFixed(2);
        const dataVencimento = new Date(fatura.data_vencimento).toLocaleDateString('pt-BR');
        htmlContent += `
          <div style="background-color: #f5f5f5; padding: 10px; margin-bottom: 10px; border-radius: 5px;">
            <div style="margin-bottom: 5px;">
              • <strong>ID:</strong> ${fatura.id} - <strong>Descrição:</strong> ${fatura.descricao}
            </div>
            <div style="color: #666; font-size: 0.9em;">
              <strong>Valor:</strong> R$ ${valor} - <strong>Status:</strong> ${fatura.status} - <strong>Vencimento:</strong> ${dataVencimento}
            </div>
          </div>
        `;
      });

      htmlContent += `</div>`;
    }

    // Instruções de resolução
    htmlContent += `
      <div style="background-color: #e3f2fd; padding: 15px; border-radius: 5px; margin-top: 20px;">
        <div style="color: #1976d2; font-weight: bold; margin-bottom: 10px;">
          ℹ️ Como resolver:
        </div>
        <ul style="margin: 0; padding-left: 20px; color: #1976d2;">
          <li style="margin-bottom: 5px;">
            <strong>Faturas:</strong> Quite todas as faturas pendentes (abertas ou vencidas)
          </li>
          <li style="margin-bottom: 5px;">
            <strong>Contratos:</strong> Todos os contratos ativos foram cancelados automaticamente
          </li>
        </ul>
      </div>
    </div>`;

    Swal.fire({
      title: '❌ Não é possível desativar o cliente',
      html: htmlContent,
      icon: 'error',
      confirmButtonText: 'Entendi',
      confirmButtonColor: '#d32f2f',
      width: '600px',
      customClass: {
        popup: 'swal-deactivate-error-popup',
        title: 'swal-deactivate-error-title',
        htmlContainer: 'swal-deactivate-error-content'
      }
    });
  };

  const handleActivate = () => {
    setIsActivating(true);
  };

  const handleConfirmActivate = async () => {
    try {
      await clientService.ativar(client!.id);
      toast.success('Cliente ativado com sucesso!');
      // Recarregar os dados do cliente
      if (resolvedClientId) {
        loadClient(resolvedClientId);
      }
    } catch (err: any) {
      console.error('Erro ao ativar cliente:', err);

      // Verificar se o erro tem detalhes
      if (err.response?.data) {
        const errorData = err.response.data;
        toast.error(errorData.message || 'Erro ao ativar cliente');
      } else {
        toast.error('Erro ao ativar cliente');
      }
    } finally {
      setIsActivating(false);
    }
  };

  const handleCancelActivate = () => {
    setIsActivating(false);
  };

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('pt-BR');
  };

  const canEdit = user?.perfil === 'administrador' || user?.permissions?.includes('clientes.editar');

  const handleNewTicket = () => {
    setTicketModalOpen(true);
  };

  const handleNewContract = () => {
    setContractModalOpen(true);
  };

  const handleNovoAtendimentoSuccess = (ticket: AtendimentoTicket) => {
    setNovoAtendimentoAberto(false);
    setTicketAtendimentoAtivo(ticket);
    navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
  };

  const handleVerAtendimento = () => {
    if (!ticketAtendimentoAtivo) return;
    navigate('/atendimento/atendimentos', { state: { ticketId: ticketAtendimentoAtivo.id } });
  };

  const handleContractSubmit = async (data: any) => {
    try {
      setIsSavingContract(true);
      const response = await contractService.create(data);
      if (response.success) {
        toast.success('Contrato criado com sucesso!');
        setContractModalOpen(false);
        // Recarregar dados do cliente para atualizar contadores e listas
        if (resolvedClientId) loadClient(resolvedClientId);
      } else {
        toast.error(response.message || 'Erro ao criar contrato');
      }
    } catch (error: any) {
      console.error('Erro ao criar contrato:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar contrato');
    } finally {
      setIsSavingContract(false);
    }
  };

  const handleAcoesMenuClose = () => {
    setAcoesMenuAnchor(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={embedded ? '40vh' : '60vh'}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Cliente não encontrado'}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Voltar para Clientes
        </Button>
      </Box>
    );
  }

  const acoesButtonColor = getStatusColor(client.status);
  const acoesButtonColorProp = acoesButtonColor === 'default' ? 'inherit' : acoesButtonColor;

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>{client.nome} - Detalhes do Cliente - SysUp</title>
        </Helmet>
      )}

      <Box sx={embedded ? { p: { xs: 1, sm: 1.5 } } : undefined}>
        {/* Header do Cliente */}
        <Box sx={{ mb: 1 }}>
          {/* Header Principal - Design Minimalista */}
          <Paper elevation={1} sx={detailPageHeaderPaperSx(theme)}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              {/* Info Principal */}
              <Box display="flex" alignItems="center" gap={2}>
                {!(embedded && hideEmbeddedClose) && (
                  <IconButton
                    onClick={handleBack}
                    color="primary"
                    sx={{
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        transform: 'translateY(-1px)',
                        boxShadow: 2,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {embedded ? <Close /> : <ArrowBack />}
                  </IconButton>
                )}
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem'
                  }}
                >
                  {client.nome.charAt(0).toUpperCase()}
                </Avatar>

                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={getStatusLabel(client.status)}
                      color={getStatusColor(client.status) as any}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                      {client.id} • {client.nome}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" color="text.secondary">
                      {client.tipo_pessoa === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'} |
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Email color="primary" sx={{ fontSize: 18 }} /> {getPrimaryEmail()} |
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Phone color="primary" sx={{ fontSize: 18 }} /> {getPrimaryCelular()} |
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Business color="primary" sx={{ fontSize: 18 }} /> {formatarCPFCNPJ(client.cpf_cnpj)} |
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <LocationOn color="primary" sx={{ fontSize: 18 }} /> {client.cidade}, {client.estado} |
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Ações */}
              {canEdit && (
                <>
                  {/* Mobile: menu estilo status */}
                  <Button
                    size="small"
                    variant="contained"
                    color={acoesButtonColorProp}
                    endIcon={<ArrowDownIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => setAcoesMenuAnchor(e.currentTarget)}
                    sx={{
                      display: { xs: 'inline-flex', sm: 'none' },
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.4,
                      fontWeight: 600,
                    }}
                  >
                    {getStatusLabel(client.status)}
                  </Button>

                  {/* Desktop: botões individuais */}
                  <Box display={{ xs: 'none', sm: 'flex' }} gap={1}>
                    {podeCriarNovoTicket && (
                      ticketAtendimentoAtivo ? (
                        <Button
                          variant="contained"
                          startIcon={<ChatIcon />}
                          onClick={handleVerAtendimento}
                          sx={{ borderRadius: 2 }}
                          size="small"
                          color="success"
                        >
                          Ver Atendimento
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<ChatIcon />}
                          onClick={() => setNovoAtendimentoAberto(true)}
                          sx={{ borderRadius: 2 }}
                          size="small"
                        >
                          Iniciar Atendimento
                        </Button>
                      )
                    )}
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleEdit}
                      sx={{ borderRadius: 2 }}
                      size="small"
                    >
                      Editar Cliente
                    </Button>
                    {client.status !== 'inativo' && (
                      <Button
                        variant="outlined"
                        startIcon={<Block />}
                        onClick={handleDeactivate}
                        sx={{ borderRadius: 2 }}
                        size="small"
                      >
                        Desativar Cliente
                      </Button>
                    )}
                    {(client.status === 'inativo' || client.status === 'suspenso') && (
                      <Button
                        variant="outlined"
                        startIcon={<CheckCircle />}
                        onClick={handleActivate}
                        sx={{ borderRadius: 2 }}
                        color="success"
                        size="small"
                      >
                        Ativar Cliente
                      </Button>
                    )}
                  </Box>

                  <Menu
                    anchorEl={acoesMenuAnchor}
                    open={acoesMenuOpen}
                    onClose={handleAcoesMenuClose}
                    PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}
                  >
                    {podeCriarNovoTicket && (
                      ticketAtendimentoAtivo ? (
                        <MenuItem
                          onClick={() => { handleAcoesMenuClose(); handleVerAtendimento(); }}
                          sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 500 }}
                        >
                          <ChatIcon sx={{ fontSize: 16, mr: 1 }} />
                          Ver Atendimento
                        </MenuItem>
                      ) : (
                        <MenuItem
                          onClick={() => { handleAcoesMenuClose(); setNovoAtendimentoAberto(true); }}
                          sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 500 }}
                        >
                          <ChatIcon sx={{ fontSize: 16, mr: 1 }} />
                          Iniciar Atendimento
                        </MenuItem>
                      )
                    )}
                    <MenuItem
                      onClick={() => { handleAcoesMenuClose(); handleEdit(); }}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      <Edit sx={{ fontSize: 16, mr: 1 }} />
                      Editar Cliente
                    </MenuItem>
                    {client.status !== 'inativo' && (
                      <>
                        <Divider />
                        <MenuItem
                          onClick={() => { handleAcoesMenuClose(); handleDeactivate(); }}
                          sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'error.main' }}
                        >
                          <Block sx={{ fontSize: 16, mr: 1 }} />
                          Desativar Cliente
                        </MenuItem>
                      </>
                    )}
                    {(client.status === 'inativo' || client.status === 'suspenso') && (
                      <>
                        <Divider />
                        <MenuItem
                          onClick={() => { handleAcoesMenuClose(); handleActivate(); }}
                          sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 500 }}
                        >
                          <CheckCircle sx={{ fontSize: 16, mr: 1 }} />
                          Ativar Cliente
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Abas */}
        <Paper elevation={2} sx={{ maxWidth: 'none', mx: 'auto', width: '100%', overflowX: 'visible', borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Abas do cliente"
              sx={detailPageTabsSx(theme)}
            >
              <Tab
                label="Visão Geral"
                icon={<Person sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(0)}
              />
              <Tab
                label="Contatos"
                icon={<ContactPhone sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(1)}
              />
              <Tab
                label="Financeiro"
                icon={<BalanceIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(2)}
              />
              <Tab
                label="Contratos"
                icon={<Assignment sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(3)}
              />
              <Tab
                label="Indicações"
                icon={<GroupsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(4)}
              />
              <Tab
                label="Chamados"
                icon={<SupportAgentIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(5)}
              />
              <Tab
                label="Atendimentos"
                icon={<HeadsetMicIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(6)}
              />
              <Tab
                label="Documentos"
                icon={<Description sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(7)}
              />
              <Tab
                label="Notas Fiscais"
                icon={<Receipt sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(8)}
              />
              <Tab
                label="Mensagens"
                icon={<ChatIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(9)}
              />
              <Tab
                label="Logs"
                icon={<History sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(10)}
              />
            </Tabs>
          </Box>

          {/* Conteúdo das Abas */}
          <TabPanel value={tabValue} index={0}>
            <ClientOverview
              client={client}
              onEdit={handleEdit}
              onNewContract={handleNewContract}
              onNewTicket={handleNewTicket}
              onNewInvoice={() => setInvoiceFormOpen(true)}
              onIniciarAtendimento={() => setNovoAtendimentoAberto(true)}
              onVerAtendimento={handleVerAtendimento}
              podeIniciarAtendimento={podeCriarNovoTicket}
              hasAtendimentoAtivo={!!ticketAtendimentoAtivo}
              onViewInvoices={() => setTabValue(2)}
              onViewContracts={() => setTabValue(3)}
              onViewTickets={() => setTabValue(5)}
              onViewAtendimentos={() => setTabValue(6)}
              onRefresh={() => loadClient(client.id)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ClientContacts clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ClientFinanceiro clienteId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ClientContracts clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <ClientIndicacoes clienteId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <ClientTickets clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <ClientAtendimentos clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            <ClientDocuments clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={8}>
            <ClientNfcomNotasTable clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={9}>
            <ClientMessages clientId={client.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={10}>
            <ClientLogs clientId={client.id} />
          </TabPanel>
        </Paper>
      </Box>

      {/* Formulário de Edição do Cliente */}
      <ClientForm
        open={isEditing}
        onClose={() => setIsEditing(false)}
        onSuccess={handleClientUpdate}
        editingClient={client}
      />

      {/* Dialog de Desativação */}
      <Dialog
        open={isActivating}
        onClose={handleCancelActivate}
        aria-labelledby="activate-dialog-title"
        aria-describedby="activate-dialog-description"
      >
        <DialogTitle id="activate-dialog-title">{"Ativar Cliente"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="activate-dialog-description">
            Tem certeza que deseja ativar o cliente "{client?.nome}"? O cliente voltará a ter acesso aos serviços.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelActivate} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmActivate} color="success" variant="contained">
            Ativar
          </Button>
        </DialogActions>
      </Dialog>

      {client && (
        <>
          <ChamadoForm
            open={ticketModalOpen}
            onClose={() => setTicketModalOpen(false)}
            clientId={client.id}
            onSuccess={() => loadClient(client.id)}
          />

          <TabbedContractForm
            open={contractModalOpen}
            onClose={() => setContractModalOpen(false)}
            clientId={client.id}
            onSubmit={handleContractSubmit}
            loading={isSavingContract}
          />

          <InvoiceMultipleForm
            open={invoiceFormOpen}
            onClose={() => setInvoiceFormOpen(false)}
            onSubmit={() => {
              setInvoiceFormOpen(false);
              loadClient(client.id);
            }}
            title="Nova Fatura"
            clientId={client.id}
          />
        </>
      )}

      <NovoAtendimentoClienteDialog
        open={novoAtendimentoAberto && podeCriarNovoTicket}
        onClose={() => setNovoAtendimentoAberto(false)}
        onSuccess={handleNovoAtendimentoSuccess}
        contatosCliente={contacts.filter((c: any) => c.is_whatsapp)}
      />

    </>
  );
};

export default ClientDetailsPage; 